<template>
  <div
    v-if="choices.length"
    class="promotion-free-item-picker"
    :style="buttonPositionStyle"
  >
    <button
      type="button"
      class="promotion-free-item-trigger"
      :aria-label="$t('retailSale.cart.chooseFreeItem')"
      :title="$t('retailSale.cart.chooseFreeItem')"
      @pointerdown.stop.prevent="startDrag"
      @click.stop="openAfterDrag"
    >
      <i class="fas fa-gift"></i>
      <span>{{ $t('retailSale.cart.chooseFreeItem') }}</span>
      <b>{{ choices.length }}</b>
    </button>

    <a-modal
      v-if="visible"
      v-model="visible"
      :closable="false"
      :keyboard="false"
      :mask-closable="false"
      :footer="null"
      :width="620"
      :z-index="1400"
      :title="null"
      wrap-class-name="promotion-free-item-modal-wrap"
      @cancel="preventClose"
    >
      <section
        class="promotion-free-item-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="free-item-modal-title"
        tabindex="-1"
        @click.stop
      >
        <header class="promotion-free-item-modal-header">
          <div>
            <h3 id="free-item-modal-title">
              <i class="fas fa-gift"></i
              >{{ $t('retailSale.cart.chooseFreeItem') }}
            </h3>
            <p>{{ $t('retailSale.cart.freeItemHint') }}</p>
          </div>
        </header>
        <div class="promotion-free-item-modal-body">
          <div
            v-for="choice in choices"
            :key="choice.condition_id"
            class="promotion-free-item-choice"
          >
            <div class="promotion-free-item-choice-header">
              <strong>{{ choice.promotion_name }}</strong>
              <span>{{ $t('retailSale.cart.selectFreeItem') }}</span>
            </div>
            <div class="promotion-free-item-options">
              <div
                v-for="item in choice.items"
                :key="item.pro_sale_code_id"
                class="promotion-free-item-option"
                role="radio"
                tabindex="0"
                :aria-checked="String(selectionValue(choice.condition_id)) === String(item.pro_sale_code_id)"
                :class="{
                  selected:
                    String(selectionValue(choice.condition_id)) ===
                    String(item.pro_sale_code_id)
                }"
                @click="select(choice, item)"
                @keydown.enter.prevent="select(choice, item)"
              >
                <span class="promotion-free-item-option-icon"
                  ><i class="fas fa-gift"></i
                ></span>
                <span class="promotion-free-item-option-copy">
                  <strong>{{ itemName(item) }}</strong>
                  <small v-if="item.sale_code || item.uom_name_en || item.uom_name_kh">
                    {{ item.sale_code }}<span v-if="item.sale_code && itemUom(item)"> · </span>{{ itemUom(item) }}
                  </small>
                  <small>{{
                    $t('retailSale.cart.freeQuantity', {
                      quantity: quantityFor(choice, item)
                    })
                  }}</small>
                </span>
                <span
                  v-if="isSelected(choice, item)"
                  class="promotion-free-item-selected-label"
                >
                  {{ $t('retailSale.cart.selectedFreeItem') }}
                </span>
                <span
                  v-if="item.is_allow_adjust_free_quantity"
                  class="promotion-free-item-quantity"
                  @click.stop
                >
                  <button
                    type="button"
                    :disabled="quantityFor(choice, item) <= 1"
                    :aria-label="$t('retailSale.cart.decreaseFreeQuantity', { item: itemName(item) })"
                    @click="changeQuantity(choice, item, -1)"
                  >−</button>
                  <b>{{ quantityFor(choice, item) }}</b>
                  <button
                    type="button"
                    :aria-label="$t('retailSale.cart.increaseFreeQuantity', { item: itemName(item) })"
                    @click="changeQuantity(choice, item, 1)"
                  >+</button>
                </span>
              </div>
            </div>
          </div>
        </div>
        <footer class="promotion-free-item-modal-footer">
          <button
            type="button"
            class="promotion-free-item-done"
            :disabled="!allSelected"
            @click="close"
          >
            {{ $t('retailSale.cart.confirmFreeItem') }}
          </button>
        </footer>
      </section>
    </a-modal>
  </div>
</template>

<script>
export default {
  name: 'PromotionFreeItemChoices',
  props: {
    choices: {
      type: Array,
      default: () => []
    },
    selections: {
      type: Object,
      default: () => ({})
    },
    quantities: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      visible: false,
      localSelections: {},
      localQuantities: {},
      dragState: null,
      dragMoved: false,
      buttonPosition: {
        left: null,
        top: null
      }
    }
  },
  computed: {
    allSelected() {
      return this.choices.every(choice =>
        this.selectionValue(choice.condition_id)
      )
    },
    buttonPositionStyle() {
      if (this.buttonPosition.left === null || this.buttonPosition.top === null) {
        return {}
      }

      return {
        left: `${this.buttonPosition.left}px`,
        top: `${this.buttonPosition.top}px`,
        right: 'auto',
        transform: 'none'
      }
    }
  },
  watch: {
    choices: {
      immediate: true,
      deep: true,
      handler(choices) {
        this.localSelections = { ...this.selections }
        this.localQuantities = { ...this.quantities }
        if (!choices.length) {
          this.visible = false
          return
        }
        if (
          choices.length &&
          choices.some(choice => !this.selectionValue(choice.condition_id))
        ) {
          this.visible = true
        }
      }
    },
    selections: {
      deep: true,
      handler(value) {
        this.localSelections = { ...value }
      }
    },
    quantities: {
      deep: true,
      handler(value) {
        this.localQuantities = { ...value }
      }
    }
  },
  mounted() {
    window.addEventListener('pointermove', this.drag)
    window.addEventListener('pointerup', this.stopDrag)
    window.addEventListener('pointercancel', this.stopDrag)
  },
  beforeDestroy() {
    window.removeEventListener('pointermove', this.drag)
    window.removeEventListener('pointerup', this.stopDrag)
    window.removeEventListener('pointercancel', this.stopDrag)
  },
  methods: {
    startDrag(event) {
      const rect = event.currentTarget.getBoundingClientRect()

      this.dragMoved = false
      this.dragState = {
        startX: event.clientX,
        startY: event.clientY,
        left: rect.left,
        top: rect.top
      }
    },
    drag(event) {
      if (!this.dragState) return

      const deltaX = event.clientX - this.dragState.startX
      const deltaY = event.clientY - this.dragState.startY
      if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) this.dragMoved = true
      if (!this.dragMoved) return

      const button = this.$el.querySelector('.promotion-free-item-trigger')
      const width = button ? button.offsetWidth : 48
      const height = button ? button.offsetHeight : 48
      const maxLeft = Math.max(window.innerWidth - width - 8, 8)
      const maxTop = Math.max(window.innerHeight - height - 8, 8)

      this.buttonPosition.left = Math.min(
        Math.max(this.dragState.left + deltaX, 8),
        maxLeft
      )
      this.buttonPosition.top = Math.min(
        Math.max(this.dragState.top + deltaY, 8),
        maxTop
      )
    },
    stopDrag() {
      if (this.dragState) this.dragState = null
    },
    openAfterDrag() {
      if (this.dragMoved) {
        this.dragMoved = false
        return
      }

      this.open()
    },
    open() {
      this.localSelections = { ...this.selections }
      this.localQuantities = { ...this.quantities }
      this.visible = true
    },
    close() {
      this.visible = false
    },
    preventClose() {
      this.visible = true
    },
    select(choice, item) {
      const conditionId = choice.condition_id
      const saleCodeId = item.pro_sale_code_id
      this.$set(this.localSelections, conditionId, saleCodeId)
      this.$emit('select', {
        conditionId,
        saleCodeId,
        promotionId: choice.promotion_id,
        quantity: this.quantityFor(choice, item)
      })
    },
    changeQuantity(choice, item, change) {
      const quantity = Math.max(1, this.quantityFor(choice, item) + change)
      const key = this.quantityKey(choice, item)
      this.$set(this.localQuantities, key, quantity)
      this.select(choice, item)
    },
    selectionValue(conditionId) {
      return this.localSelections[conditionId] || ''
    },
    isSelected(choice, item) {
      return String(this.selectionValue(choice.condition_id)) === String(item.pro_sale_code_id)
    },
    quantityKey(choice, item) {
      return `promotion-${choice.promotion_id}-${choice.condition_id}-${item.pro_sale_code_id}`
    },
    quantityFor(choice, item) {
      return Number(this.localQuantities[this.quantityKey(choice, item)] || item.quantity || 1)
    },
    itemUom(item) {
      const isKhmer = this.$root.$i18n && this.$root.$i18n.locale === 'kh'

      return isKhmer
        ? item.uom_name_kh || item.uom_name_en || ''
        : item.uom_name_en || item.uom_name_kh || ''
    },
    itemName(item) {
      const isKhmer = this.$root.$i18n && this.$root.$i18n.locale === 'kh'

      return isKhmer
        ? item.product_name_kh || item.product_name_en
        : item.product_name_en || item.product_name_kh
    }
  }
}
</script>

<style scoped>
.promotion-free-item-picker {
  position: fixed;
  width: 48px;
  height: 48px;
  z-index: 1200;
  top: 50%;
  right: 18px;
  transform: translateY(-50%);
}
.promotion-free-item-trigger {
  position: relative;
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #93c5fd;
  border-radius: 50%;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 18px;
  box-shadow: 0 8px 22px rgba(30, 64, 175, 0.22);
  cursor: grab;
  touch-action: none;
}
.promotion-free-item-trigger:active {
  cursor: grabbing;
}
.promotion-free-item-trigger:hover {
  border-color: #2563eb;
  background: #dbeafe;
}
.promotion-free-item-trigger span {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}
.promotion-free-item-trigger b {
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 18px;
  padding: 3px 5px;
  border: 2px solid #ffffff;
  border-radius: 10px;
  background: #dc2626;
  color: #ffffff;
  font-size: 10px;
  line-height: 1;
  text-align: center;
}
.promotion-free-item-backdrop {
  position: fixed;
  z-index: 1400;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 22px;
  background: rgba(15, 23, 42, 0.68);
}
.promotion-free-item-modal {
  width: 100%;
  max-height: min(720px, calc(100vh - 44px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}
::v-deep .promotion-free-item-modal-wrap .ant-modal-body {
  padding: 0;
}
.promotion-free-item-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #dbeafe;
  background: linear-gradient(135deg, #eff6ff, #ffffff);
}
.promotion-free-item-modal-header h3 {
  margin: 0;
  color: #1e3a8a;
  font-size: 18px;
  line-height: 1.25;
}
.promotion-free-item-modal-header h3 i {
  margin-right: 7px;
  color: #2563eb;
}
.promotion-free-item-modal-header p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.45;
}
.promotion-free-item-modal-body {
  display: grid;
  gap: 12px;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px;
}
.promotion-free-item-choice {
  padding: 9px;
  border: 1px solid #dbeafe;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.82);
}
.promotion-free-item-choice-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 7px;
}
.promotion-free-item-choice-header strong {
  overflow: hidden;
  color: #334155;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.promotion-free-item-choice-header span {
  flex: 0 0 auto;
  color: #64748b;
  font-size: 10px;
}
.promotion-free-item-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(145px, 1fr));
  gap: 7px;
}
.promotion-free-item-option {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  min-height: 62px;
  padding: 9px;
  border: 1px solid #dbe3ec;
  border-radius: 6px;
  background: #ffffff;
  color: #334155;
  cursor: pointer;
  text-align: left;
}
.promotion-free-item-option:hover {
  border-color: #93c5fd;
  background: #f8fbff;
}
.promotion-free-item-option.selected {
  border-color: #2563eb;
  background: #eff6ff;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}
.promotion-free-item-option-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  flex: 0 0 27px;
  border-radius: 50%;
  background: #dbeafe;
  color: #2563eb;
  font-size: 11px;
}
.promotion-free-item-option-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}
.promotion-free-item-option-copy strong {
  overflow: hidden;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.promotion-free-item-option-copy small {
  color: #64748b;
  font-size: 10px;
}
.promotion-free-item-option-copy small + small {
  color: #1d4ed8;
  font-weight: 700;
}
.promotion-free-item-selected-label {
  align-self: flex-start;
  padding: 3px 5px;
  border-radius: 4px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 9px;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
}
.promotion-free-item-quantity {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  margin-left: auto;
}
.promotion-free-item-quantity button {
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid #bfdbfe;
  border-radius: 4px;
  background: #ffffff;
  color: #1d4ed8;
  cursor: pointer;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
}
.promotion-free-item-quantity button:hover {
  border-color: #2563eb;
  background: #dbeafe;
}
.promotion-free-item-quantity button:focus,
.promotion-free-item-quantity button:focus-visible {
  outline: none;
  box-shadow: none;
}
.promotion-free-item-quantity button:disabled {
  border-color: #e2e8f0;
  background: #f8fafc;
  color: #94a3b8;
  cursor: not-allowed;
}
.promotion-free-item-quantity b {
  min-width: 18px;
  color: #1e3a8a;
  font-size: 11px;
  text-align: center;
}
.promotion-free-item-modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 13px 20px;
  border-top: 1px solid #dbeafe;
  background: #f8fbff;
}
.promotion-free-item-done {
  min-width: 108px;
  padding: 9px 15px;
  border: 0;
  border-radius: 5px;
  background: #1459b8;
  color: #ffffff;
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
}
.promotion-free-item-done:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

@media (max-width: 576px) {
  .promotion-free-item-backdrop {
    align-items: flex-end;
    padding: 10px;
  }
  .promotion-free-item-modal {
    width: 100%;
    max-height: calc(100vh - 20px);
    border-radius: 10px 10px 0 0;
  }
  .promotion-free-item-modal-header {
    padding: 15px 16px;
  }
  .promotion-free-item-modal-body {
    padding: 13px 16px;
  }
  .promotion-free-item-modal-footer {
    padding: 12px 16px;
  }
  .promotion-free-item-picker {
    right: 10px;
    width: 44px;
    height: 44px;
  }
  .promotion-free-item-trigger {
    width: 44px;
    height: 44px;
    font-size: 16px;
  }
  .promotion-free-item-choice-header {
    align-items: flex-start;
    flex-direction: column;
  }
  .promotion-free-item-options {
    grid-template-columns: 1fr;
  }
}
</style>
